import { ref } from "vue";
import ExcelJS from "exceljs";
import { useApolloClient } from "@vue/apollo-composable";
import gql from "graphql-tag";
import { BasicOrganizationType, UserProfileRoleType } from "@/graphql/types";
import { buildExportFileName } from "./utils";

const BATCH_SIZE = 20;

const HEADERS = [
  "OrganisationsID",
  "Organisationsnavn",
  "CVR-nummer",
  "userId",
  "firstname",
  "lastname",
  "email",
  "mitID",
  "kombitID",
  "eID",
  "administrator",
  "disabled",
  "lastLogin",
  "creationDate",
  "Role Organisationsnavn",
  "Role ApplicationNavn",
  "Role RoleNavn",
  "Role Attribute",
  "Kommentar",
];

const USER_PROFILES_EXCEL_QUERY = gql`
  query userProfilesExcelBatch($organizationIds: [ID]) {
    userProfilesExcel(organizationIds: $organizationIds) {
      user {
        userId
        administrator
        organization {
          organizationId
          name
        }
        email
        firstname
        lastname
        mitID
        kombitID
        eID
        disabled
        lastLogin
        creationDate
      }
      organizationCVR
      userRoles {
        roleName
        roleAttribute
        organizationName
        organizationVat
        applicationName
        isOnBehalf
      }
    }
  }
`;

function formatUTCDateTime(value: string | null | undefined): string {
  if (!value) return "";
  const d = new Date(value);
  if (isNaN(d.getTime())) return value;
  const M = d.getMonth() + 1;
  const day = d.getDate();
  const yyyy = d.getFullYear();
  const H = d.getHours();
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${M}/${day}/${yyyy} ${H}:${mm}`;
}

function resolveCreationDate(profile: UserProfileRoleType): string {
  if (profile.user?.creationDate)
    return formatUTCDateTime(profile.user.creationDate);
  const hasExternalId =
    profile.user?.mitID || profile.user?.kombitID || profile.user?.eID;
  return hasExternalId ? "1/9/2025" : "1/1/2025";
}

function flattenToRows(profiles: UserProfileRoleType[]): unknown[][] {
  const rows: unknown[][] = [];

  for (const profile of profiles) {
    const u = profile.user;
    const creationDate = resolveCreationDate(profile);

    const userCells = [
      u?.organization?.organizationId ?? "",
      u?.organization?.name ?? "",
      profile.organizationCVR ?? "",
      u?.userId ?? "",
      u?.firstname ?? "",
      u?.lastname ?? "",
      u?.email ?? "",
      u?.mitID ?? "",
      u?.kombitID ?? "",
      u?.eID ?? "",
      u?.administrator ?? false,
      u?.disabled ?? false,
      formatUTCDateTime(u?.lastLogin),
      creationDate,
    ];

    if (!profile.userRoles || profile.userRoles.length === 0) {
      rows.push([...userCells, "", "", "", "", "No assigned roles"]);
    } else {
      for (const role of profile.userRoles) {
        rows.push([
          ...userCells,
          role?.organizationName ?? "",
          role?.applicationName ?? "",
          role?.roleName ?? "",
          role?.roleAttribute ?? "",
          "",
        ]);
      }
    }
  }

  return rows;
}

async function buildWorkbook(
  allProfiles: UserProfileRoleType[]
): Promise<ArrayBuffer> {
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet("Organizations");

  const dataRows = flattenToRows(allProfiles);

  // Add all rows up front so we can measure column widths across all data
  const headerRow = ws.addRow(HEADERS);
  const addedRows = dataRows.map((row) =>
    ws.addRow(row as ExcelJS.CellValue[])
  );

  // Bold header row with white text
  headerRow.font = { bold: true, color: { argb: "FFFFFFFF" } };

  // Apply table style (TableStyleMedium9 matches ClosedXML's default blue-banded style)
  ws.addTable({
    name: "BrugerProfilerTable",
    ref: "A1",
    headerRow: true,
    totalsRow: false,
    style: {
      theme: "TableStyleMedium2",
      showRowStripes: true,
    },
    columns: HEADERS.map((h) => ({ name: h, filterButton: true })),
    rows: dataRows as ExcelJS.CellValue[][],
  });

  // Auto-fit column widths based on the longest value in each column
  ws.columns.forEach((col, colIndex) => {
    let maxLen = HEADERS[colIndex]?.length ?? 10;
    for (const row of addedRows) {
      const cell = row.getCell(colIndex + 1);
      const val = cell.value != null ? String(cell.value) : "";
      if (val.length > maxLen) maxLen = val.length;
    }
    col.width = Math.min(maxLen + 2, 60); // +2 padding, cap at 60
  });

  return wb.xlsx.writeBuffer() as Promise<ArrayBuffer>;
}

function triggerDownload(buffer: ArrayBuffer, fileName: string): void {
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  a.click();
  URL.revokeObjectURL(url);
}

export default function useExportAllProfilesToExcel() {
  const isExporting = ref(false);
  const exportedCount = ref(0);
  const totalCount = ref(0);
  const batchFrom = ref(0);
  const finishedCount = ref(0);

  const { client } = useApolloClient();

  async function fetchProfilesForBatch(
    organizationIds: string[]
  ): Promise<UserProfileRoleType[]> {
    const result = await client.query({
      query: USER_PROFILES_EXCEL_QUERY,
      variables: { organizationIds },
      fetchPolicy: "no-cache",
    });
    return (result?.data?.userProfilesExcel ?? []).filter(
      Boolean
    ) as UserProfileRoleType[];
  }

  async function exportAll(
    organizations: (BasicOrganizationType | null | undefined)[]
  ) {
    if (isExporting.value) return;

    const orgsWithId = organizations
      .filter((o): o is BasicOrganizationType => !!o?.organizationId)
      .map((o) => o.organizationId as string);

    if (orgsWithId.length === 0) return;

    isExporting.value = true;
    exportedCount.value = 0;
    totalCount.value = orgsWithId.length;
    batchFrom.value = 0;
    finishedCount.value = 0;

    const allProfiles: UserProfileRoleType[] = [];

    try {
      for (let i = 0; i < orgsWithId.length; i += BATCH_SIZE) {
        const batch = orgsWithId.slice(i, i + BATCH_SIZE);
        batchFrom.value = i;
        exportedCount.value = Math.min(i + BATCH_SIZE, orgsWithId.length);
        const profiles = await fetchProfilesForBatch(batch);
        allProfiles.push(...profiles);
        finishedCount.value = Math.min(i + BATCH_SIZE, orgsWithId.length);
      }

      const buffer = await buildWorkbook(allProfiles);
      triggerDownload(buffer, buildExportFileName("Users_List_Export"));
    } finally {
      isExporting.value = false;
    }
  }

  async function exportSingleOrg(organizationId: string) {
    if (isExporting.value) return;

    isExporting.value = true;
    try {
      const profiles = await fetchProfilesForBatch([organizationId]);
      const buffer = await buildWorkbook(profiles);
      triggerDownload(buffer, buildExportFileName("Users_List_Export"));
    } finally {
      isExporting.value = false;
    }
  }

  return {
    exportAll,
    exportSingleOrg,
    isExporting,
    exportedCount,
    totalCount,
    batchFrom,
    finishedCount,
  };
}
